import { useMutation } from '@apollo/client';
import { UPDATE_CREATIVE_PROJECT } from '../../graphql/mutations/creativeMutations';

/**
 * Hook to update an existing Creative Project
 * @returns {object} - { handleUpdateProject, loading, error }
 */
export const useUpdateCreativeProject = () => {
  const [updateProjectMutation, { loading, error }] = useMutation(UPDATE_CREATIVE_PROJECT, {
    refetchQueries: ['GetCreativeProject', 'CreativeProjectsByCreativeID', 'GetCreative'],
    awaitRefetchQueries: true
  });

  const sanitizeInput = (input = {}) => {
    const cleaned = { ...input };
    Object.keys(cleaned).forEach((key) => {
      const value = cleaned[key];
      if (key === 'studioID' && value === null) {
        return;
      }
      if (
        value === undefined || 
        value === null ||
        (Array.isArray(value) && value.length === 0 && (key === 'images' || key === 'audios'))
      ) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  const handleUpdateProject = async (projectData) => {
    try {
      if (!projectData?.id) {
        throw new Error('Das Projekt konnte nicht zugeordnet werden. Bitte lade die Seite neu und versuche es erneut.');
      }

      // Build input object with ONLY the fields we want - never include images/audios unless explicitly needed
      const finalInput = {
        id: projectData.id,
        creativeID: projectData.creativeID,
        title: projectData.title,
        description: projectData.description,
        status: projectData.status
      };

      // Only add optional fields if they exist and have valid values
      if (projectData.viewCount !== undefined && projectData.viewCount !== null) {
        finalInput.viewCount = projectData.viewCount;
      }
      
      if (projectData.category !== undefined && projectData.category !== null && projectData.category !== '') {
        finalInput.category = projectData.category;
      }
      
      if (projectData.tags !== undefined && projectData.tags !== null && 
          Array.isArray(projectData.tags) && projectData.tags.length > 0) {
        finalInput.tags = projectData.tags;
      }
      
      if (projectData.studioID !== undefined) {
        finalInput.studioID =
          projectData.studioID === '' ? null : projectData.studioID;
      }

      // Only include images if it exists and has content (for VISUAL type)
      // IMPORTANT: Only add if it's a valid non-empty array
      if (projectData.images && 
          Array.isArray(projectData.images) && projectData.images.length > 0) {
        finalInput.images = projectData.images;
      }
      // Do NOT add images property if it doesn't exist or is empty

      // Only include audios if it exists and has content (for MUSIC type)
      // IMPORTANT: Only add if it's a valid non-empty array
      if (projectData.audios && 
          Array.isArray(projectData.audios) && projectData.audios.length > 0) {
        finalInput.audios = projectData.audios;
      }
      // Do NOT add audios property if it doesn't exist or is empty
      if (projectData.artists && 
          Array.isArray(projectData.artists) && projectData.artists.length > 0) {
        finalInput.artists = projectData.artists;
      }

      // Only include genre if it exists (for MUSIC type)
      if (projectData.genre && projectData.genre !== '') {
        finalInput.genre = projectData.genre;
      }

      // Final sanitization pass - remove any undefined/null values that might have slipped through
      Object.keys(finalInput).forEach((key) => {
        const value = finalInput[key];
        if (key === 'studioID' && value === null) {
          return;
        }
        if (value === undefined || value === null || 
            ((key === 'images' || key === 'audios') && Array.isArray(value) && value.length === 0)) {
          delete finalInput[key];
        }
      });

      const input = sanitizeInput(finalInput);

      const variables = sanitizeInput({
        input,
        condition: projectData?.condition
      });

      const { data: projectResponse } = await updateProjectMutation({
        variables
      });

      return projectResponse?.updateCreativeProject;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Helper to update project status
   */
  const updateProjectStatus = async (projectId, newStatus, condition) => {
    if (!newStatus) {
      throw new Error('Der Projektstatus konnte nicht gesetzt werden. Bitte versuche es erneut.');
    }
    return handleUpdateProject({
      id: projectId,
      status: newStatus,
      condition
    });
  };

  /**
   * Helper to increment view count
   */
  const incrementViewCount = async (projectId, currentViewCount, condition) => {
    return handleUpdateProject({
      id: projectId,
      viewCount: (currentViewCount || 0) + 1,
      condition
    });
  };

  return {
    handleUpdateProject,
    updateProjectStatus,
    incrementViewCount,
    loading,
    error
  };
};
